import { Injectable } from '@angular/core';

import { ServiceError } from '../../exceptions/service.error';

import { BackendBaseService } from '../backend-base.service';
import { HttpClientService } from '../common/http-client.service';

@Injectable()
export class OutgoingJobService extends  BackendBaseService {
  /**
   * Base URL for controller
   * @type {string}
   */
  url: string = 'outgoing_jobs';

  /**
   * Constructor
   * @param {HttpClientService} private httpClient Service for making HTTP requests with different methods
   */
  constructor(public httpClient: HttpClientService) {
    super(httpClient);
  }

  /**
   * Cancel outgoing job
   * @param  {number}       id Outgoing job id
   * @return {Promise<any>}    Server response
   */
  cancel(id: number): Promise<any> {
    return this.httpClient.put(`${this.url}/${id}/cancel.json`, {});
  }

  /**
   * Create outgoing job
   * @param  {number}       document_id Id of document for the outgoing job
   * @param  {any}          params      Outgoing job params
   * @return {Promise<any>}             Server response
   */
  create(params: any): Promise<any> {
    if(!params.document_id) {
      throw new ServiceError('Outgoing job create missing document id');
    }
    return this.httpClient.post(`/documents/${params.document_id}/create_outgoing_job.json`, params);
  }

  /**
   * Delete lock file for outgoing jobs
   * @return {Promise<any>} Server response
   */
  deleteLockFile(): Promise<any> {
    return this.httpClient.get(`${this.url}/delete_lockfile.json`);
  }

  /**
   * Complete outgoing jobs from index search results
   * @param  {any}          params Params to get search results
   * @return {Promise<any>}        Server response
   */
  finish(params: any = {}): Promise<any> {
    return this.httpClient.get(`${this.url}/finish.json`, params);
  }

  /**
   * Get default search options for index page
   * @return {Promise<any>} Server response
   */
  getOutgoingJobsSearchOptions(): Promise<any> {
    return this.httpClient.get(`${this.url}.json`, { get_search_options: true });
  }

  /**
   * Print outgoing job - from index or from show
   * @param  {number}       id Id of outgoing job to print
   * @return {Promise<any>}    Server response
   */
  print(id: number | null = null, params: any = {}): Promise<any> {
    if (id) return this.httpClient.put(`${this.url}/${id}/print.json`, {});
    else return this.httpClient.get(`${this.url}/print.json`, params);
  }

  /**
   * Load outgoing job details without reusing stale browser-cached JSON.
   * @param  {number}       id Id of outgoing job
   * @return {Promise<any>}    Server response
   */
  show(id: number): Promise<any> {
    return this.httpClient.get(`${this.url}/${id}.json`, { _: Date.now() });
  }

  /**
   * Release outgoing job
   * @param  {number}       id Id of outgoing job
   * @return {Promise<any>}    Server response
   */
  release(id: number): Promise<any> {
    return this.httpClient.put(`${this.url}/${id}/release.json`, {});
  }

  /**
   * Restart outgoing job
   * @param  {number}       id Id of outgoing job
   * @return {Promise<any>}    Server response
   */
  restart(id: number): Promise<any> {
    return this.httpClient.put(`${this.url}/${id}/restart.json`, {});
  }

  /**
   * Send out the outgoing job
   * @param  {number}       id Id of outgoing job
   * @return {Promise<any>}    Server response
   */
  sendout(id: number): Promise<any> {
    return this.httpClient.put(`${this.url}/${id}/sendout.json`, {});
  }

  /**
   * Start outgoing job when stalled or erred
   * @param  {number}       id Id of outgoing job
   * @return {Promise<any>}    Server response
   */
  start(id: number): Promise<any> {
    return this.httpClient.put(`${this.url}/${id}/start.json`, {});
  }

  /**
   * Take outgoing job
   * @param  {number}       id Id of outgoing job
   * @return {Promise<any>}    Server response
   */
  take(id: number): Promise<any> {
    return this.httpClient.put(`${this.url}/${id}/take.json`, {});
  }
}
